/**
 * English translations
 */
export const en = {
  // Landing Page
  landing: {
    title: 'KEYBOARD VISUALIZER',
    subtitle: 'Unlock Professional Speed.',
    description: 'The ultimate learning platform visualizing 1,300+ shortcuts in real-time. Master your tools, stay in the flow, and maximize your creative output.',
    ctaButton: 'Get Started for Free',

    stats: {
      shortcuts: 'Shortcuts Included',
      apps: 'Professional Apps',
      levels: 'Skill Levels',
    },

    whyTitle: 'Why Keyboard Visualizer?',

    features: {
      visualFeedback: {
        title: 'Immersive Visualization',
        description: 'Every keystroke is beautifully rendered. Bridge the gap between brain and fingers with intuitive visual feedback.',
      },
      quizMode: {
        title: 'Active Learning Quiz',
        description: 'Retain knowledge through practice based on active recall. Let your muscle memory take over the repetitive tasks.',
      },
      multiPlatform: {
        title: 'Master Every Tool',
        description: 'From VS Code to Excel and Gmail. Become a master of the professional software you use every single day.',
      },
      dashboard: {
        title: 'Learning Dashboard',
        description: 'Visualize your progress with statistical charts and AI-driven analysis of your weak shortcuts.',
      },
      conflictDetection: {
        title: 'Conflict Detection',
        description: 'Automatically detect application and browser shortcut conflicts with real-time guidance and solutions.',
      },
      sequentialInput: {
        title: 'Sequential Input',
        description: 'Full support for advanced shortcuts requiring sequential keystrokes like Ctrl + K followed by Ctrl + S.',
      },
    },

    appsTitle: 'Supported Applications',
    appsDescription: 'Comprehensive shortcut sets for the essential tools of modern professionals.',

    benefitsTitle: 'Benefits You\'ll Gain',
    benefits: {
      productivity: {
        number: '01',
        title: 'Exponential Productivity',
        description: 'Replacing mouse actions with keyboard shortcuts increases your workflow speed by an average of 30-50%.',
      },
      flow: {
        number: '02',
        title: 'Uninterrupted Flow State',
        description: 'Minimize visual and physical context switching. Keep your focus where it belongs: on your creative work.',
      },
      skill: {
        number: '03',
        title: 'Professional Mastery',
        description: 'Fluent keyboard operation is a mark of true expertise, projecting confidence and technical proficiency.',
      },
      ergonomics: {
        number: '04',
        title: 'Ergonomic Well-being',
        description: 'Reduce repetitive strain from mouse usage. Protect your wrists and shoulders for a longer, healthier career.',
      },
    },

    targetTitle: 'Who is it for?',
    targets: {
      engineer: {
        title: 'Engineers / Developers',
        description: 'Speed up editor operations and focus on solving complex problems, not fighting the UI.',
      },
      designer: {
        title: 'Designers / Creators',
        description: 'Make tool switching subconscious. Let your creativity flow directly from mind to canvas.',
      },
      business: {
        title: 'Business Professionals',
        description: 'Blitz through documents and emails. Spend your time on high-value tasks that actually matter.',
      },
    },

    faqTitle: 'Frequently Asked Questions',
    faqs: [
      {
        q: 'Is it really free to use?',
        a: 'Yes, all features are completely free. You can start learning immediately without even creating an account.'
      },
      {
        q: 'Can I learn OS-specific shortcuts?',
        a: 'Absolutely. We support both Windows and macOS. You can switch your preference in the settings at any time.'
      },
      {
        q: 'Do I need to install anything?',
        a: 'No installation required. It runs entirely in your browser, so you can practice anywhere, anytime.'
      }
    ],

    testimonialsTitle: 'User Testimonials',
    testimonials: [
      {
        name: 'K. S. (Software Engineer)',
        comment: 'I could never remember VS Code shortcuts, but thanks to this app, I mastered the main ones in a week. My development speed has increased dramatically!',
        rating: 5,
      },
      {
        name: 'M. T. (Designer)',
        comment: 'The visual feedback of the keys is very intuitive. I want to use it for Figma and Photoshop practice too. The quiz format keeps me engaged.',
        rating: 5,
      },
      {
        name: 'Y. S. (University Student)',
        comment: 'It runs smoothly in the browser, so I practice during my spare time. Not just typing speed, but knowing shortcuts made my overall PC operation much faster.',
        rating: 4,
      },
      {
        name: 'S. I. (Administrative Staff)',
        comment: 'I started using this to learn Excel shortcuts. I reach for the mouse less often, and I feel like my stiff shoulders have improved a bit (lol).',
        rating: 5,
      },
      {
        name: 'E. Rodriguez (Content Writer)',
        comment: 'My browser and text editor operations have become dramatically smoother. Especially tab management and text editing shortcuts are essential for maintaining focus during writing.',
        rating: 5,
      },
      {
        name: 'T. Nakamura (System Administrator)',
        comment: 'It\'s invaluable for learning command-line operations for server management. Even in critical tasks, having shortcuts ingrained makes me feel more confident.',
        rating: 5,
      },
    ],

    howItWorksTitle: 'How It Works',
    steps: {
      step1: {
        number: 'STEP 1',
        title: 'Select Application',
        description: 'Choose the application and level you want to learn',
      },
      step2: {
        number: 'STEP 2',
        title: 'Press Keys',
        description: 'Input shortcuts with your keyboard',
      },
      step3: {
        number: 'STEP 3',
        title: 'Instant Feedback',
        description: 'Correct/incorrect answers are displayed instantly, deepening your understanding',
      },
    },

    releaseNotesTitle: 'Latest Updates',
    releaseNotesDescription: 'Latest features and improvements for Keyboard Visualizer',
    viewAllReleases: 'View All Release Notes →',

    finalCtaTitle: 'Start now and maximize your productivity',
    finalCtaDescription: 'Completely free, no registration required. Start using immediately',
    finalCtaButton: 'Get Started for Free',

    footer: {
      copyright: 'Keyboard Visualizer. All rights reserved.',
    },
  },

  // App Header
  header: {
    title: 'Keyboard Visualizer',
    login: 'Log In',
    logout: 'Log Out',
    profile: 'Profile',
    settings: 'Settings',
    releaseNotes: 'Release Notes',
    backToHome: 'Back to Home',
    backToLP: 'Back to LP',
    quizMode: 'Quiz Mode',
    visualizer: 'Visualizer',
    fullscreen: 'Fullscreen',
    exitFullscreen: 'Exit Fullscreen',
    csvDownload: 'Download CSV',
    noShortcutsToDownload: 'No shortcuts to download',
    returnToVisualizer: 'Return to visualizer',
    startQuizMode: 'Start quiz mode',
    keyboardLayout: 'Keyboard Layout',
  },

  // Setup Screen
  setup: {
    title: 'Keyboard Visualizer',
    subtitle: 'Shortcut Learning Tool',
    welcome: 'Welcome!',
    welcomeMessage: 'Visualize and practice over 1,300 shortcuts',
    loginButton: 'Log In',
    loginTooltip: 'Log in to save quiz progress',
    selectLanguage: 'Select Language',
    selectDisplayMode: 'Select Display Mode',
    selectKeyboardLayout: 'Select Keyboard Layout',
    selectMode: 'Select Mode',
    selectApplication: 'Select Application (Multiple selection allowed)',
    selectQuizApplication: 'Select Application for Quiz',
    selectDifficulty: 'Select Difficulty Level',
    confirmButton: 'Confirm',
    randomApp: 'Random',
    pleaseSelectDisplayMode: 'Please select display mode',
    pleaseSelectLayout: 'Please select keyboard layout',
    pleaseSelectMode: 'Please select mode',
    pleaseSelectApp: 'Please select application',
    pleaseSelectDifficulty: 'Please select difficulty',
    pleaseSelectQuizApp: 'Please select application for quiz',
    canChangeInSettings: 'You can change these settings later',
    displayMode: {
      fullscreen: {
        title: 'Fullscreen Mode',
        description: 'Focus on learning with fullscreen display',
        recommendation: '💡 Recommended: More immersive learning experience',
      },
      windowed: {
        title: 'Windowed Mode',
        description: 'Use alongside other windows',
      },
    },
    keyboardLayout: {
      windowsJis: {
        title: 'Windows JIS',
        description: 'Japanese Keyboard (Windows)',
      },
      windowsUs: {
        title: 'Windows US',
        description: 'US (English) Keyboard (Windows)',
      },
      macJis: {
        title: 'Mac JIS',
        description: 'Japanese Keyboard (Mac)',
      },
      macUs: {
        title: 'Mac US',
        description: 'US (English) Keyboard (Mac)',
      },
    },
    modes: {
      visualizer: {
        title: 'Visualizer Mode',
        description: 'Visualize keyboard shortcuts in real-time',
      },
      quiz: {
        title: 'Quiz Mode',
        description: 'Learn shortcuts and compete for scores',
      },
    },
    difficulty: 'Difficulty Level',
    difficultyOptions: {
      basic: {
        name: 'basic',
        description: 'For beginners: Commonly used basic shortcuts',
      },
      standard: {
        name: 'standard',
        description: 'For intermediate users: Standard difficulty shortcuts',
      },
      hard: {
        name: 'hard',
        description: 'For advanced users: More advanced and practical shortcuts',
      },
      madmax: {
        name: 'madmax',
        description: 'Expert level: Very specialized and difficult professional shortcuts',
      },
      allrange: {
        name: 'allrange',
        description: 'All difficulties: Random shortcuts from all levels',
      },
    },
    start: 'Start',
    back: 'Back',
  },

  // Quiz
  quiz: {
    title: 'Quiz Mode',
    question: 'Question',
    score: 'Score',
    progress: 'Progress',
    inputShortcut: 'Input the shortcut for this action',
    correct: 'Correct!',
    incorrect: 'Incorrect',
    skip: 'Skip',
    next: 'Next',
    nextQuestion: 'Next Question →',
    finish: 'Finish',
    pause: 'Pause',
    resume: 'Resume',
    paused: 'Paused',
    sequential: 'Sequential',
    pressKeysInOrder: 'Press keys in order (hold Alt and press sequentially)',
    pressCorrectShortcut: 'Press the correct shortcut keys',
    questionFormat: '[{app}] What is the shortcut for "{description}"?',
    typing: 'Typing...',
    yourAnswer: 'Your answer:',
    correctAnswer: 'Correct answer:',
    otherCorrect: 'Other correct answers:',
    results: {
      title: 'Quiz Results',
      yourScore: 'Your Score',
      correctAnswers: 'Correct Answers',
      totalQuestions: 'Total Questions',
      accuracy: 'Accuracy',
      tryAgain: 'Try Again',
      backToSetup: 'Back to Setup',
      quizComplete: '🎉 Quiz Complete!',
      difficultShortcuts: 'Difficult Shortcuts',
      mistakesCount: 'mistakes',
      retryQuiz: '🔄 Try Again',
      shareResults: '🔗 Share Results',
      copied: '✅ Copied!',
      selectOtherQuiz: '📝 Select Other Quiz',
      backToStart: '🏠 Back to Start',
      shareText: 'I scored {correctAnswers}/{totalQuestions} on the {app} keyboard shortcut quiz! Accuracy: {accuracy}% #KeyboardVisualizer',
    },
  },

  // Normal Mode (Visualization)
  normalMode: {
    title: 'Visualization Mode',
    pressKeys: 'Press any keyboard shortcut',
    pressAnyKey: 'Press any key...',
    noMatch: 'No matching shortcut',
    noShortcutFound: 'No shortcut found',
    protectedShortcut: 'This is a system-protected shortcut',
    fullscreenHint: 'Press F11 to enable fullscreen mode and capture more shortcuts',
    applicationSelector: 'Application',
    difficultyFilter: 'Difficulty Filter',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    selectAtLeastOne: '⚠️ Please select at least one difficulty',
    availableShortcuts: 'Available Shortcuts',
    singleKeyShortcuts: 'Single Key Shortcuts',
    gmailSingleKeyShortcuts: 'Gmail Single Key Shortcuts',
    sequential: 'Sequential',
    browserConflict: 'Browser Conflict',
    browserConflictWarning: 'Browser Shortcut Conflict',
    browserConflictDescription: 'These keys may conflict with Chrome browser shortcuts (resolved in fullscreen mode)',
    preventableInFullscreen: 'Preventable in Fullscreen',
    systemProtected: 'System Protected',
    difficultyDescriptions: {
      basic: 'Daily basic operations',
      standard: 'Standard shortcuts',
      hard: 'Advanced features and operations',
      madmax: 'Expert level shortcuts',
    },
  },

  // Shortcut Card
  shortcutCard: {
    basic: 'Basic',
    standard: 'Standard',
    hard: 'Hard',
    madmax: 'Mad Max',
    windows: 'Windows',
    mac: 'macOS',
    crossPlatform: 'Cross-Platform',
    protected: 'System Protected',
    preventableInFullscreen: 'Preventable in Fullscreen',
    browserConflict: 'Browser Shortcut Conflict (Fullscreen to resolve)',
  },

  // Authentication
  auth: {
    signIn: 'Sign In',
    signUp: 'Sign Up',
    signOut: 'Sign Out',
    passwordReset: 'Reset Password',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    displayName: 'Display Name',
    forgotPassword: 'Forgot Password?',
    noAccount: 'Don\'t have an account?',
    haveAccount: 'Already have an account?',
    signInWith: 'Sign in with',
    signInWithGoogle: 'Sign in with Google',
    connectingToGoogle: 'Connecting to Google...',
    google: 'Google',
    github: 'GitHub',
    or: 'or',
    emailSignIn: 'Sign in with Email',
    loading: 'Loading...',
    processing: 'Processing...',
    sendResetLink: 'Send Reset Link',
    backToSignIn: 'Back to Sign In',
    subtitle: 'Create an account to save your quiz progress and scores',
    passwordResetSubtitle: 'Enter your registered email address. We will send you a password reset link.',
    passwordResetTitle: 'Password Reset',
    newPasswordLabel: 'New Password',
    confirmPasswordLabel: 'Confirm New Password',
    resetPasswordButton: 'Reset Password',
    setNewPasswordMessage: 'Please set your new password.',
    linkInvalidError: 'Password reset link is invalid or expired. Please request a new one.',
    resetSuccessMessage: 'Password reset successfully. Redirecting to home in 3 seconds...',
    confirmationEmailSent: 'Confirmation email sent. Please check your email to activate your account.',
    passwordResetEmailSent: 'Password reset email sent. Please check your email.',
    signOutError: 'Failed to sign out. Please try again.',
    displayNamePlaceholder: 'e.g., John Doe',
    passwordPlaceholder: '6+ characters',
    myPage: 'My Page',
    defaultUserName: 'User',
    error: {
      invalidEmail: 'Invalid email address',
      weakPassword: 'Password must be at least 6 characters',
      passwordMismatch: 'Passwords do not match',
      userNotFound: 'User not found',
      wrongPassword: 'Incorrect password',
      emailInUse: 'Email already in use',
      unknownError: 'An error occurred. Please try again.',
      invalidCredentials: 'Invalid email or password.',
      emailNotConfirmed: 'Email not confirmed. Please check your inbox.',
      userExists: 'This email address is already registered.',
      passwordTooShort: 'Password must be at least 6 characters.',
      rateLimit: 'Too many requests. Please wait a moment.',
      sessionInvalid: 'Session invalid. Please sign in again.',
      oauthCancelled: 'Sign in was cancelled.',
      oauthProviderError: 'An error occurred with the social login provider.',
      serverError: 'A server error occurred. Please try again later.',
      temporarilyUnavailable: 'Service temporarily unavailable. Please try again later.',
      invalidRequest: 'Invalid request. Please try again.',
      unauthorizedClient: 'Login provider not configured correctly. Please contact admin.',
      invalidScope: 'Permission settings issue. Please contact admin.',
      genericSocialError: 'An error occurred during social login.',
    },
  },

  // User Menu
  user: {
    welcome: 'Welcome',
    myProfile: 'My Profile',
    quizHistory: 'Quiz History',
    statistics: 'Statistics',
    settings: 'Settings',
    signOut: 'Sign Out',
  },

  // Statistics
  stats: {
    totalSessions: 'Total Sessions',
    averageScore: 'Average Score',
    totalCorrect: 'Total Correct',
    totalQuestions: 'Total Questions',
    overallAccuracy: 'Overall Accuracy',
    lastQuiz: 'Last Quiz',
    noData: 'No quiz history yet',
  },

  // My Page Components
  myPage: {
    dashboard: 'Dashboard',
    welcomeBack: 'Your learning progress at a glance',
    backToApp: '🏠 Back to App',
    statsSummary: '📊 Statistics Summary',
    appPerformanceTitle: '📈 App Performance',
    weakShortcutsTitle: '⚠️ Review Focus Shortcuts',
    bookmarkedShortcutsTitle: '⭐ Bookmarks',
    startBookmarkedDrill: 'Practice Bookmarks',
    noBookmarks: 'No bookmarked shortcuts yet.',
    appStatsTitle: '📱 App Statistics',
    recentHistoryTitle: '🎯 Recent History',
    profileTitle: '👤 Profile',
    avatarAlt: 'Avatar',
    edit: 'Edit',
    uploadConfirm: 'Confirm Upload',
    uploading: 'Uploading...',
    uploadSuccess: 'Avatar uploaded successfully!',
    displayNamePlaceholder: 'Display Name',
    displayNameUpdated: 'Display name updated!',
    displayNameEmpty: 'Display name cannot be empty.',
    bioPlaceholder: 'Bio (e.g., I want to master shortcuts to improve my productivity as an engineer)',
    goalPlaceholder: 'Goal (e.g., Master all Excel shortcuts)',
    bioLabel: 'Bio',
    goalLabel: 'Goal',
    profileUpdated: 'Profile updated!',
    settingsTitle: '⚙️ Settings',
    changeEmail: 'Change Email',
    newEmailPlaceholder: 'New Email',
    changeButton: 'Change',
    emailUpdateSent: 'Confirmation email sent to your new address.',
    emailSameError: 'New email is the same as current.',
    invalidEmailError: 'Please enter a valid email address.',
    passwordTitle: 'Password',
    passwordResetLink: 'Reset password here →',
    dangerZoneTitle: 'Danger Zone',
    deleteAccountButton: 'Delete Account',
    deleteConfirmText: 'Are you sure you want to delete your account?',
    deleteConfirmButton: 'Delete',
    deleteCancelButton: 'Cancel',
    deleteFailed: 'Failed to delete account: ',
    fetchDataFailed: 'Failed to fetch quiz data: ',
    analyzing: 'Analyzing...',
    noWeakShortcuts: 'Perfect! No weak shortcuts found.',
    accuracy: 'Accuracy',
    mistakes: 'Miss {wrong} / {total} tries',
    startDrill: 'Start Focus Drill',
    noSessions: 'No recent quiz sessions.',
    noStats: 'You haven\'t played any quizzes yet.',
    app: 'Application',
    sessions: 'Sessions',
    sessionsShort: 'Sessions',
    lastPlayed: 'Last Played',
    difficulty: 'Difficulty',
    score: 'Score',
    correctCount: 'Correct / Questions',
    date: 'Date',
    details: 'Details',
    overallAccuracy: 'Overall Accuracy',
    correct: 'Correct',
    totalSessions: 'Total Sessions',
    completedQuizzes: 'Completed Quizzes',
    totalAnswers: 'Total Answers',
    questionsSolved: 'Questions Solved',
    questionsSolvedShort: 'Qs',
  },

  // Session Detail Modal
  sessionDetail: {
    title: 'Quiz Session Details',
    failedToFetch: 'Failed to fetch session details: ',
    summary: 'Summary',
    accuracy: 'Accuracy:',
    correctAnswers: 'Correct Answers:',
    history: 'Answer History',
    noHistory: 'No answer history available',
    table: {
      shortcut: 'Shortcut',
      description: 'Description',
      result: 'Result',
      time: 'Time',
    },
    resultCorrect: '✓ Correct',
    resultIncorrect: '✗ Incorrect',
  },

  // System Shortcut Warning
  systemShortcutWarning: {
    modalTitle: 'About macOS System Shortcuts',
    buttonTitle: 'About macOS System Shortcut Settings',
    description: 'macOS system shortcuts (like <code>Ctrl + F2</code>) cannot be disabled by web applications.',
    solutionTitle: 'You can resolve conflicts using the following methods:',
    method1Title: 'Method 1: Use Fullscreen Mode',
    method1Desc: 'By putting the app in <strong>fullscreen mode</strong>, you can capture some shortcuts. Please click the fullscreen button in the top right.',
    method2Title: 'Method 2: Disable in System Settings',
    method2Step1: 'Open System Settings → Keyboard → Keyboard Shortcuts',
    method2Step2: 'Select the relevant category from the left menu:',
    method2Categories: {
      missionControl: 'Mission Control',
      launchpad: 'Launchpad',
      keyboard: 'Keyboard',
      screenshots: 'Screenshots',
    },
    method2Step3: 'Uncheck shortcuts you don\'t use',
    commonConflictsTitle: 'Main Conflicting Shortcuts',
    canBeDisabledHint: 'These can be disabled in "System Settings"',
    securityHint: '💡 <strong>Hint:</strong> Security-related shortcuts (like <code>Cmd + Option + Escape</code>, <code>Ctrl + Cmd + Q</code>) are protected by the OS and cannot be disabled.',
  },

  // Common
  common: {
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    back: 'Back',
    retry: 'Retry',
    next: 'Next',
    previous: 'Previous',
    search: 'Search',
    filter: 'Filter',
    sort: 'Sort',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
  },

  // Errors
  errors: {
    notFound: 'Page not found',
    serverError: 'Server error',
    networkError: 'Network error',
    unknownError: 'Unknown error',
    tryAgain: 'Please try again',
    failedToFetch: 'Failed to fetch data',
    checkConnection: 'Please check your internet connection or try again later.',
  },

  // Home Page
  home: {
    loadingTitle: 'Loading Keyboard Shortcuts...',
    loadingSubtitle: 'Preparing data. Please wait a moment.',
  },

  // Release Notes
  releaseNotes: {
    title: 'Release Notes',
    subtitle: 'Latest updates and improvement history for Keyboard Visualizer',
    backToHome: '← Back to Home',
    categories: {
      feature: 'New Feature',
      improvement: 'Improvement',
      fix: 'Bug Fix',
      breaking: 'Breaking Change',
    },
  },
};

export type Translations = typeof en;
